import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { CodeSentryFetcherName } from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import type { DependencyScope } from '../../../../shared/dependenciesV2';

import useGetReleaseDependencyConfidences from './useGetReleaseDependencyConfidences';
import useGetRevisionDependencyConfidences from './useGetRevisionDependencyConfidences';

/**
 * Helper hook for fetching the confidence of a dependency of either a user's revision or release.
 * @param scope - dependency scope - used to find the revision or the release.
 * @param dependencyLocatorStr - The locator of a dependency.
 * @returns result of query for the confidence of the dependency.
 */
export default function useGetDependencyConfidenceByDependencyScope(
  scope: DependencyScope | null,
  dependencyLocatorStr: string,
) {
  const isBinaryScanningEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isBinaryComponent = Locator.parse(dependencyLocatorStr).fetcher === CodeSentryFetcherName;

  const projectRevision = scope && scope.scope === 'project' ? scope.revision : '';
  const isGetRevisionEnabled = isBinaryScanningEnabled && isBinaryComponent && !!projectRevision;
  const {
    data: projectConfidenceData,
    isLoading: isProjectConfidenceDataLoading,
  } = useGetRevisionDependencyConfidences(projectRevision, isGetRevisionEnabled);

  const releaseId = scope && scope.scope === 'releaseGroup' ? scope.projectGroupReleaseId : -1;
  const isGetReleaseEnabled = isBinaryScanningEnabled && isBinaryComponent && releaseId > 0;
  const {
    data: releaseConfidenceData,
    isLoading: isReleaseConfidenceDataLoading,
  } = useGetReleaseDependencyConfidences(releaseId, isGetReleaseEnabled);

  // otherwise returns isLoading true
  if (!isBinaryScanningEnabled) {
    return {
      confidence: null,
      isLoading: false,
      isEnabled: false,
    };
  }

  if (scope && scope.scope === 'releaseGroup') {
    const releaseConfidenceMap = releaseConfidenceData?.confidences ?? {};
    return {
      confidence: releaseConfidenceMap[dependencyLocatorStr] ?? null,
      isLoading: isReleaseConfidenceDataLoading,
      isEnabled: true,
    };
  }

  const projectConfidenceMap = projectConfidenceData?.confidences ?? {};
  return {
    confidence: projectConfidenceMap[dependencyLocatorStr] ?? null,
    isLoading: isProjectConfidenceDataLoading,
    isEnabled: true,
  };
}
